import {
  input,
  Input,
  computed,
  Component,
  booleanAttribute,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  NgpRadioIndicator,
  NgpRadioItem,
  injectRadioGroupState,
} from 'ng-primitives/radio';
import { NgpFocus } from 'ng-primitives/interactions';

/**
 * Radio values aren't always strings — call sites bind enum/number ids
 * (sentiment `-1|0|1`, mass-update ops) and booleans (`true/false` mode flags).
 * NgpRadioGroup compares by `===` (or `compareWith`), so any primitive works at
 * runtime; this just keeps `strictTemplates` honest.
 */
export type SuiRadioValue = string | number | boolean;

@Component({
  selector: 'sui-radio',
  standalone: true,
  imports: [NgpRadioItem, NgpRadioIndicator, NgpFocus],
  templateUrl: './sui-radio.component.html',
  styleUrl: './sui-radio.component.scss',
  // `data-sui-*` — ours. Every other data-* on a sui host comes from ng-primitives.
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { '[attr.data-sui-indicator]': 'indicatorPosition' },
})
export class SuiRadioComponent {
  @Input() value!: SuiRadioValue;

  /**
   * `leading` (default) for form contexts; `trailing` right-pins the dot for rows
   * in a floating panel, matching `sui-menu-item [selected]` and combobox options.
   */
  @Input() indicatorPosition: 'leading' | 'trailing' = 'leading';

  /** Per-item disable. The item is also disabled when the whole group is. */
  readonly disabled = input(false, { transform: booleanAttribute });

  private readonly groupState = injectRadioGroupState();

  // NgpRadioItem never guards onClick/onFocus against disabled, and group-level
  // disable does NOT cascade to items — so compute it ourselves and drive the
  // native `disabled` (which actually blocks click + focus).
  protected readonly effectiveDisabled = computed(
    () => this.disabled() || (this.groupState()?.disabled() ?? false),
  );
}
